/**
 * features/finance/useGlData.ts
 * ------------------------------------------------------------------
 * Self-subscribing hook for the general ledger. Mirrors useVoucherData.
 *
 * ACCOUNTS are unscoped — the chart is small (tens of rows) and every screen
 * needs it to render a code as a name.
 *
 * JOURNALS are SCOPED BY FINANCIAL YEAR, for the same reason vouchers are:
 * they accumulate permanently, so an unbounded listener grows without limit.
 * `fy` is carried on every journal from day one precisely so this query can
 * exist without a migration later.
 * ------------------------------------------------------------------ */
import { useEffect, useState } from 'react';
import { collection, onSnapshot, query, where, orderBy } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import {
  GL_COLLECTIONS, POSTING_MAP_ID,
  type GlAccount, type GlJournal, type GlPostingMap,
} from '@/lib/gl';

export interface GlData {
  accounts: GlAccount[];
  journals: GlJournal[];
  postingMap: GlPostingMap | null;
  /** code -> account, for rendering a line without a find() per row. */
  byCode: Map<string, GlAccount>;
  loading: boolean;
  error: string | null;
}

/**
 * Pass this instead of a financial year to subscribe to the CHART ONLY.
 *
 * A screen that renders account codes but never reads a journal — a take-on, a
 * chart editor — has no reason to hold a listener on the journal collection,
 * which is already megabytes and grows with every posting. Blocking a render on
 * it is a spinner that gets slower every day of the school year.
 */
export const CHART_ONLY = '';

export function useGlData(fy: string): GlData {
  const [accounts, setAccounts] = useState<GlAccount[]>([]);
  const [journals, setJournals] = useState<GlJournal[]>([]);
  const [postingMap, setPostingMap] = useState<GlPostingMap | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubs: Array<() => void> = [];

    unsubs.push(
      onSnapshot(
        query(collection(db, GL_COLLECTIONS.accounts), orderBy('sortOrder')),
        (snap) => {
          setAccounts(snap.docs.map((d) => d.data() as GlAccount));
          setLoading(false);
        },
        (e) => { setError(e.message); setLoading(false); },
      ),
    );

    unsubs.push(
      onSnapshot(
        collection(db, GL_COLLECTIONS.postingMap),
        (snap) => {
          const d = snap.docs.find((x) => x.id === POSTING_MAP_ID);
          setPostingMap(d ? (d.data() as GlPostingMap) : null);
        },
        (e) => setError(e.message),
      ),
    );

    return () => unsubs.forEach((u) => u());
  }, []);

  useEffect(() => {
    // CHART_ONLY — no year, no journal listener. See the constant above.
    if (!fy) { setJournals([]); return; }
    const unsub = onSnapshot(
      query(collection(db, GL_COLLECTIONS.journals), where('fy', '==', fy)),
      (snap) => {
        // Sorted client-side: ordering by date here would need a composite
        // index for a collection this small, and the FY filter already bounds it.
        const rows = snap.docs.map((d) => d.data() as GlJournal);
        rows.sort((a, b) => (b.date || '').localeCompare(a.date || '') || (b.journalNo || '').localeCompare(a.journalNo || ''));
        setJournals(rows);
      },
      (e) => setError(e.message),
    );
    return () => unsub();
  }, [fy]);

  const byCode = new Map(accounts.map((a) => [a.code, a]));
  return { accounts, journals, postingMap, byCode, loading, error };
}
